import { Box, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import logo from "../../../../../assets/images/marcaAccess.png";
import {
  AvatarList,
  ChatContainer,
  Logo,
  videoContainer,
  ZoomContainer,
  ZoomViewsContainer
} from "../../../../styled/ZoomContainer";
import MediaControlsContainer from "../../buttons/MediaControlsContainer";
import ProfesionalNotifications from "../../profesional/ProfesionalNotifications";
import ChatComponent from "../components/Chat";
import { RoomContext } from "../context/roomContext";
import { useCanvasDimension } from "../hooks/useCanvasDimension";
import useConsentPolling from "../hooks/useConsentPolling";
import { useGalleryLayout } from "../hooks/useGalleryLayout";
import useHandleZoomEvents from "../hooks/useHandleZoomEvents";
import useHeight from "../hooks/useHeight";
import AvatarsContainer from "./AvatarsContainer";
import OtherVideoContainer from "./OtherVideoContainer";
import SelfViewContainer from "./SelfViewContainer";
import ShareView from "./ShareView";

const ZoomParticipantContainer = () => {
  const zmClient = useContext(RoomContext);
  const mediaStream = zmClient?.getMediaStream();
  const [messages, setMessages] = useState([]);
  const [isRecieveSharing, setIsRecieveSharing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false); // Estado para controlar la visibilidad del chat
  const [unreadMessages, setUnreadMessages] = useState(0); // Contador de mensajes no leídos
  const [participants, setParticipants] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [zoomChat, setZoomChat] = useState(null);
  const { apellidoNombrePaciente, apellidoNombreProfesional } = useSelector(
    (state) => state.call.call.data,
  );
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const selfViewRef = useRef(null);
  const shareViewRef = useRef({ selfShareRef: null });
  const isChatOpenRef = useRef(isChatOpen);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  const height = useHeight();
  const canvasDimension = useCanvasDimension(
    mediaStream,
    canvasRef,
    isRecieveSharing,
  );

  const participanteLocal = zmClient?.getCurrentUserInfo();
  const haFinalizadoSesion = !zmClient.getSessionInfo()?.isInMeeting;

  const esPaciente = participanteLocal?.userIdentity === "Paciente";
  const esProfesional = participanteLocal?.userIdentity === "Profesional";
  const noIngresoProfesional = zmClient
    .getAllUser()
    .every((user) => user.userIdentity !== "Profesional");
  const deberiaEstarEnSalaEspera = esPaciente && noIngresoProfesional;
  useConsentPolling(zmClient, esProfesional);

  const { visibleParticipants, layout: videoLayout } = useGalleryLayout(
    zmClient,
    mediaStream,
    true,
    videoRef,
    canvasDimension,
    deberiaEstarEnSalaEspera,
  );

  const tieneViewportPocaAltura = useMediaQuery('(max-height:560px)');
  const hayParticipantesRemotos =
    visibleParticipants.filter((u) => u.userId !== participanteLocal?.userId)
      .length >= 1;
  const esLayoutMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const noRenderizarParticipanteLocal =
    (tieneViewportPocaAltura && hayParticipantesRemotos) ||
    (esLayoutMobile && isRecieveSharing);

  // Hook que se encarga de ligar las funciones que manejan los eventos de Zoom.
  useHandleZoomEvents({
    zmClient,
    apellidoNombrePaciente,
    apellidoNombreProfesional,
    esPaciente,
    zoomChat,
    isChatOpenRef,
    setParticipants,
    setUnreadMessages,
    setMessages,
    setUploadProgress
  });
  useEffect(() => {
    setParticipants(zmClient.getAllUser());
    const chatClient = zmClient.getChatClient();
    setZoomChat(chatClient);
    return () => {

      window.removeEventListener("beforeunload", () => zmClient.leave());
      zmClient.leave();
    };
  }, [zmClient]);

  useEffect(() => {
    if (haFinalizadoSesion) {
      const siteUrl = window.location.origin;
      window.location.href = siteUrl;
    }
  }, [zmClient, haFinalizadoSesion]);

  // Función para mostrar/ocultar el chat
  const toggleChat = () => {
    setIsChatOpen((prev) => {
      if (!prev) setUnreadMessages(0);
      return !prev;
    });
  };
  return (
    <ZoomContainer height={height} deberiaEstarEnSalaEspera={deberiaEstarEnSalaEspera}>
      <ZoomViewsContainer
        isRecieveSharing={isRecieveSharing}
        esLayoutMobile={esLayoutMobile}
        deberiaEstarEnSalaEspera={deberiaEstarEnSalaEspera}
      >
        <ShareView
          ref={shareViewRef}
          onRecieveSharingChange={setIsRecieveSharing}
        />
        <Box
          sx={(theme) => ({
            ...videoContainer,
          })}>
          {deberiaEstarEnSalaEspera && (
            <Logo src={logo} alt="Logo AccessInformática" />
          )}
          {/* el video-player-container es donde se van a renderizar todos */}
          {/* los videos de los demas participantes */}
          <canvas
            ref={canvasRef}
            style={{ width: "100%", height: "100%", position: "absolute" }}
          />
          <OtherVideoContainer
            videoLayout={videoLayout}
            visibleParticipants={visibleParticipants}
            canvasDimension={canvasDimension}
            participanteLocal={participanteLocal}
            ref={videoRef}
          />

          <SelfViewContainer
            id="self-view-video"
            ref={selfViewRef}
            videoLayout={videoLayout}
            visibleParticipants={visibleParticipants}
            canvasDimension={canvasDimension}
            noRenderizarParticipanteLocal={noRenderizarParticipanteLocal}
            esLayoutMobile={esLayoutMobile}
            deberiaEstarEnSalaEspera={deberiaEstarEnSalaEspera}
            participanteLocal={participanteLocal}
          />
          <AvatarList>
            <AvatarsContainer
              visibleParticipants={visibleParticipants}
              videoLayout={videoLayout}
              participanteLocal={participanteLocal}
              noRenderizarParticipanteLocal={noRenderizarParticipanteLocal}
              esLayoutMobile={esLayoutMobile}
              deberiaEstarEnSalaEspera={deberiaEstarEnSalaEspera}
              tieneViewportPocaAltura={tieneViewportPocaAltura}
            />
          </AvatarList>
        </Box>
        {deberiaEstarEnSalaEspera && <ProfesionalNotifications />}
      </ZoomViewsContainer>
      <MediaControlsContainer
        room={zmClient}
        selfShareCanvas={shareViewRef.current?.selfShareRef}
        deberiaEstarEnSalaEspera={deberiaEstarEnSalaEspera}
        selfViewRef={selfViewRef}
        messages={messages}
        toggleChat={toggleChat}
        isChatOpen={isChatOpen}
        unreadMessages={unreadMessages}
        tieneViewportPocaAltura={tieneViewportPocaAltura}
      />
      {isChatOpen && !deberiaEstarEnSalaEspera && (
        <ChatContainer id="chatContainer" isChatOpen>
          <ChatComponent
            messages={messages}
            uploadProgress={uploadProgress}
            participants={participants}
            zoomChat={zoomChat}
            visibleParticipants={visibleParticipants}
            toggleChat={toggleChat}
          />
        </ChatContainer>
      )}
    </ZoomContainer>
  );
};

export default ZoomParticipantContainer;
